import { useState } from 'react';

import { isGeocodingConfigured } from './geocoding';
import { LocationSearch } from './LocationSearch';
import type { MapCentreState } from './useMapCentre';
import { Button, Spinner, Text } from '../ui';

import './locationBar.css';

/** Props for {@link LocationBar}. */
export interface LocationBarProps {
  /** The centre state from {@link import('./useMapCentre').useMapCentre}. */
  readonly state: MapCentreState;
}

/**
 * Explains, in words, which place the results are for — and offers the two ways to change it.
 *
 * This exists because the failure it prevents is invisible otherwise. A map centred on the launch
 * metro looks identical to a map centred on you, so somebody in another state reads "no events
 * near you" as "this app has no events", and leaves. Naming the place turns a dead end into an
 * obvious next click.
 *
 * @param props - The component props.
 * @returns The location bar element.
 */
export function LocationBar({ state }: LocationBarProps): React.JSX.Element {
  const [isSearching, setIsSearching] = useState(false);
  const canSearch = isGeocodingConfigured();

  if (isSearching) {
    return (
      <div className="gm-location-bar gm-location-bar--searching">
        <LocationSearch
          onSelect={(centre) => {
            state.setSearchedLocation(centre);
            setIsSearching(false);
          }}
          onCancel={() => setIsSearching(false)}
        />
        <Button onClick={() => setIsSearching(false)}>Cancel</Button>
      </div>
    );
  }

  return (
    <div className="gm-location-bar">
      <div className="gm-location-bar__summary" aria-live="polite">
        {state.status === 'locating' ? (
          <>
            <Spinner label="Finding your location" />
            <Text as="span">Finding your location…</Text>
          </>
        ) : (
          <Text as="span">
            Showing events near <strong>{state.centre.label}</strong>
          </Text>
        )}
      </div>

      <div className="gm-location-bar__actions">
        {state.source !== 'device' && state.canLocate && state.status !== 'denied' && (
          <Button
            onClick={state.requestDeviceLocation}
            disabled={state.status === 'locating'}
          >
            Use my location
          </Button>
        )}

        {canSearch && <Button onClick={() => setIsSearching(true)}>Change</Button>}

        {state.source !== 'default' && (
          <Button variant="secondary" onClick={state.reset}>
            Reset
          </Button>
        )}
      </div>

      {state.status === 'denied' && (
        <div className="gm-location-bar__note">
          <Text muted size="sm">
            Location is blocked for this site. Allow it in your browser&rsquo;s site settings, or
            search for a place instead.
          </Text>
        </div>
      )}

      {state.status === 'unavailable' && (
        <div className="gm-location-bar__note">
          <Text muted size="sm">
            Could not get your location. Try again, or search for a place instead.
          </Text>
        </div>
      )}
    </div>
  );
}
